import { Fragment, ReactElement, ReactNode, useState } from 'react';
import { formatNumber } from './breakdownFormat';

export interface ExpandableRow {
  label: string;
  description: string;
  work: number;
  power: number;
  expandedContent: ReactNode;
}

interface Props {
  duration: number;
  distance?: number;
  mainRows: ExpandableRow[];
  aeroWork: number;
  aeroPower: number;
  alphaRow: ExpandableRow;
  cda: number;
}

function ToggleButton({
  isExpanded,
  onToggle,
}: {
  isExpanded: boolean;
  onToggle: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={isExpanded ? 'Collapse' : 'Expand'}
      className={
        'cr-w-6 cr-h-6 cr-flex cr-items-center cr-justify-center cr-rounded ' +
        'cr-border cr-border-gray-300 cr-bg-white cr-text-gray-700 cr-text-sm cr-font-bold ' +
        'hover:cr-bg-blue-50 hover:cr-border-blue-500 hover:cr-text-blue-700'
      }
    >
      {isExpanded ? '−' : '+'}
    </button>
  );
}

export default function WorkPowerTable({
  duration,
  distance,
  mainRows,
  aeroWork,
  aeroPower,
  alphaRow,
  cda,
}: Props): ReactElement {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (label: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  return (
    <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md cr-mb-6">
      <h3 className="font-montserrat cr-text-lg cr-font-semibold cr-text-blue-900 cr-mb-3">
        Work / Power Summary
      </h3>
      <div className="cr-flex cr-flex-wrap cr-gap-x-6 cr-gap-y-1 cr-text-sm cr-text-gray-600 cr-mb-3">
        <span>
          <span className="cr-font-medium cr-text-gray-800">Time:</span>{' '}
          {formatNumber(duration, 2)} s
        </span>
        {distance !== undefined && (
          <span>
            <span className="cr-font-medium cr-text-gray-800">Distance:</span>{' '}
            {formatNumber(distance, 1)} m
          </span>
        )}
      </div>
      <table className="cr-w-full cr-text-sm">
        <thead>
          <tr className="cr-border-b cr-border-gray-300 cr-text-gray-700">
            <th className="cr-w-8"></th>
            <th className="cr-text-left cr-py-2 cr-pr-4">Component</th>
            <th className="cr-text-left cr-py-2 cr-pr-4">Description</th>
            <th className="cr-text-right cr-py-2 cr-pr-4">Work (J)</th>
            <th className="cr-text-right cr-py-2 cr-pr-4">Power (W)</th>
            <th className="cr-py-2"></th>
          </tr>
        </thead>
        <tbody>
          {mainRows.map((row) => {
            const isExpanded = expanded.has(row.label);
            return (
              <Fragment key={row.label}>
                <tr className="cr-border-b cr-border-gray-100">
                  <td className="cr-py-2 cr-text-center">
                    <ToggleButton isExpanded={isExpanded} onToggle={() => toggle(row.label)} />
                  </td>
                  <td className="cr-py-2 cr-pr-4 cr-font-medium">{row.label}</td>
                  <td className="cr-py-2 cr-pr-4 cr-text-gray-500">{row.description}</td>
                  <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono">
                    {formatNumber(row.work, 0)}
                  </td>
                  <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono">
                    {formatNumber(row.power, 1)}
                  </td>
                  <td className="cr-py-2"></td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={6} className="cr-bg-gray-50 cr-px-4 cr-py-3 cr-border-b cr-border-gray-100">
                      {row.expandedContent}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          <tr>
            <td colSpan={6} className="cr-py-0">
              <div className="cr-border-t-2 cr-border-blue-900 cr-mt-1" />
            </td>
          </tr>
          <tr className="cr-bg-blue-50">
            <td className="cr-py-2"></td>
            <td className="cr-py-2 cr-pr-4 cr-font-semibold cr-text-blue-900">Aero</td>
            <td className="cr-py-2 cr-pr-4 cr-text-gray-600 cr-italic">
              sum of rider + kinetic + gravitational + rolling
            </td>
            <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono cr-font-semibold">
              {formatNumber(aeroWork, 0)}
            </td>
            <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono cr-font-semibold">
              {formatNumber(aeroPower, 1)}
            </td>
            <td
              rowSpan={2}
              className="cr-py-2 cr-pl-4 cr-border-l cr-border-gray-300 cr-align-middle cr-text-center"
            >
              <div className="cr-text-xs cr-text-gray-500 cr-uppercase cr-tracking-wide">CdA</div>
              <div className="cr-text-2xl cr-font-bold cr-text-blue-900 cr-font-mono">
                {formatNumber(cda, 3)}
              </div>
              <div className="cr-text-xs cr-text-gray-500">m²</div>
              <div className="cr-text-xs cr-text-gray-500 cr-mt-1 cr-italic">= Aero ÷ Alpha</div>
            </td>
          </tr>
          <tr className="cr-bg-blue-50 cr-border-b cr-border-gray-200">
            <td className="cr-py-2 cr-text-center">
              <ToggleButton
                isExpanded={expanded.has('Alpha')}
                onToggle={() => toggle('Alpha')}
              />
            </td>
            <td className="cr-py-2 cr-pr-4 cr-font-semibold cr-text-blue-900">Alpha</td>
            <td className="cr-py-2 cr-pr-4 cr-text-gray-600 cr-italic">{alphaRow.description}</td>
            <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono cr-font-semibold">
              {formatNumber(alphaRow.work, 0)}
            </td>
            <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono cr-font-semibold">
              {formatNumber(alphaRow.power, 1)}
            </td>
          </tr>
          {expanded.has('Alpha') && (
            <tr>
              <td colSpan={6} className="cr-bg-gray-50 cr-px-4 cr-py-3">
                {alphaRow.expandedContent}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="cr-text-xs cr-text-gray-500 cr-mt-3 cr-italic">
        Power = Work ÷ Time. Click + on any row to see the derivation. Rolling and Gravitational
        can be negative depending on terrain. All values computed client-side from raw inputs and
        current config values.
      </p>
    </div>
  );
}
